import React, { forwardRef, useState } from 'react';

import type { AlineFontName } from '@aline/common';
import { fontTypes } from '@aline/common';
import type { SelectChangeEvent } from '@mui/material';
import { ListItemIcon, ListItemText, MenuItem } from '@mui/material';
import clsx from 'clsx';
import { AlineTooltip, ToolbarInput, ToolbarSelect } from 'src/components';
import { CheckMarkIcon } from 'src/components/icons/CheckMarkIcon';
import { useEditorTabStateSelector } from 'src/editor/editor-tab-state-registry';
import { ClientTransforms } from 'src/editor/transforms';
import { useCurrentUserId } from 'src/gql';
import { useAuthorizedTransforms } from 'src/hooks';
import { useEditor, useEditorLoading } from 'src/layout-engine';
import { makeStyles } from 'tss-react/mui';

const useListStyles = makeStyles<StyleProps>()((_, { fontFamily }) => ({
  listItem: {
    fontFamily,
  },
}));

interface StyleProps {
  fontFamily: string;
}

interface ListItemProps {
  displayName: AlineFontName;
  fontFamily: string;
}

function ListItem({ displayName, fontFamily }: ListItemProps): JSX.Element {
  const { classes } = useListStyles({ fontFamily });

  return (
    <ListItemText
      classes={{ primary: clsx(displayName !== 'Symbol' && classes.listItem) }}
      primary={displayName}
    />
  );
}

interface FontTypeInputProps {
  open: boolean;
}

/**
 * Note we must spread ...rest for the input to render as Material-UI injects props.
 */
const FontTypeInput = forwardRef(function FontTypeInput(
  props: FontTypeInputProps,
  ref
): JSX.Element {
  const { open, ...rest } = props;

  return (
    <ToolbarInput
      ref={ref}
      inputStyles={{
        height: 24,
        margin: '0px 6px 0px 6px',
        width: 115,
      }}
      open={open}
      {...rest}
    />
  );
});

export const fontTypeSelectAttribute = 'toolbar-select-font-type';
export const fontTypeSpanAttribute = 'font-type-select-span';
export const fontTypeMenuAttribute = 'font-type-menu';
export function fontTypeMenuItemAttribute(dataAttribute: string): string {
  return `${dataAttribute}-font-type-menu-item`;
}

const useStyles = makeStyles()(theme => ({
  gutters: {
    paddingLeft: 12,
    paddingRight: 12,
  },
  horizontalDivider: {
    backgroundColor: theme.palette.divider,
  },
  listIcon: {
    minWidth: theme.spacing(4),
  },
  menu: {
    maxHeight: 720,
  },
  root: {
    fontSize: 14,
  },
}));

export const FontTypeSelect = React.memo(function FontTypeSelect(): JSX.Element {
  const editor = useEditor();
  const { classes } = useStyles();
  const currentUserId = useCurrentUserId();
  const editorLoading = useEditorLoading();
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);
  const editingMode = useEditorTabStateSelector(state => state.documentUi.editingMode);
  const fontType = useEditorTabStateSelector(state => state.derived.fontProperties.fontType);

  function handleOpen(): void {
    setTooltipOpen(false);
    setMenuOpen(true);
  }

  function handleClose(): void {
    setMenuOpen(false);
  }

  function handleTooltipOpen(): void {
    setTooltipOpen(true);
  }

  function handleTooltipClose(): void {
    setTooltipOpen(false);
  }

  function handleSelectChange(
    event: SelectChangeEvent<unknown>, // Use SelectChangeEvent here
    _child: React.ReactNode
  ): void {
    if (!currentUserId || !editingMode) return;

    const {
      target: { value },
    } = event;

    AuthorizedTransforms.setFontType(editor, {
      authorId: currentUserId,
      editingMode,
      type: value as string,
    });
    ClientTransforms.focus(editor);
  }

  return (
    <AlineTooltip
      arrow
      disableHoverListener
      open={tooltipOpen && !menuOpen}
      placement="bottom"
      title="Font"
      onClose={handleTooltipClose}
      onOpen={handleTooltipOpen}
    >
      <ToolbarSelect
        MenuProps={{ className: classes.menu }}
        disabled={editorLoading}
        input={<FontTypeInput open={menuOpen} />}
        menuAttribute={fontTypeMenuAttribute}
        open={menuOpen}
        selectAttribute={fontTypeSelectAttribute}
        selectStyles={{ paddingLeft: 8 }}
        spanAttribute={fontTypeSpanAttribute}
        sx={{
          '& .MuiSelect-select': {
            height: '24px !important',
            paddingBottom: '0px !important',
            paddingTop: '0px !important',
          },
        }}
        value={fontType}
        onClose={handleClose}
        onMouseEnter={(): void => {
          setTooltipOpen(true);
        }}
        onMouseLeave={(): void => {
          setTooltipOpen(false);
        }}
        onOpen={handleOpen}
        onSelectChange={handleSelectChange}
      >
        {fontTypes.map(({ dataAttribute, fontFamily, type }) => (
          <MenuItem
            key={dataAttribute}
            dense
            classes={{
              gutters: classes.gutters,
              root: classes.root,
            }}
            data-cy={fontTypeMenuItemAttribute(dataAttribute)}
            value={type}
          >
            {fontType === type ? (
              <ListItemIcon className={classes.listIcon}>
                <CheckMarkIcon />
              </ListItemIcon>
            ) : (
              <ListItemIcon className={classes.listIcon}>
                <div />
              </ListItemIcon>
            )}
            <ListItem displayName={type} fontFamily={fontFamily} />
          </MenuItem>
        ))}
      </ToolbarSelect>
    </AlineTooltip>
  );
});
